import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass';
import { FiUpload, FiPlus, FiEdit2, FiX } from 'react-icons/fi';

import { FT_COLORS } from '../theme';
import { imageUrlFromAttrs, cropParamsFromAttrs } from '../helpers';
import MediaRow from './MediaRow';
import EditPhotoModal from './EditPhotoModal';
import CroppedAvatar from './CroppedAvatar';
import { destroy } from 'utils/api';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';

const FieldLabel = ({ children }) => (
  <Text
    mb={1}
    sx={{
      color: 'codGrey', fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, lineHeight: '20px',
    }}
  >
    {children}
  </Text>
);
FieldLabel.propTypes = { children: PropTypes.node.isRequired };

// Formik field for the "Profile Photo": a compact Upload row that opens the Edit
// Photo modal (CoverField cropper). We keep the ORIGINAL file + crop params on the
// `avatar` value; MemberFormModal uploads the original and PATCHes crop/zoom.
const PhotoField = ({ field, form, label, circular, currentCover, currentImage, onRemoved }) => {
  const [open, setOpen] = useState(false);
  const [removed, setRemoved] = useState(false);
  const { receiveDeletion } = useEntityActionCreators();
  const value = field.value;

  // A freshly chosen file (Blob directly, or the cropper's { file } object).
  const newFile = value instanceof Blob ? value : (value && value.file) || null;

  const newThumb = useMemo(
    () => (newFile instanceof Blob ? URL.createObjectURL(newFile) : ''),
    [newFile],
  );

  // Crop/zoom source: the in-progress cropper value if edited, else the existing image.
  const cropSource = value && typeof value === 'object' && !(value instanceof Blob)
    ? value
    : (removed ? null : currentImage);
  const thumbUrl = newThumb || (removed ? '' : imageUrlFromAttrs(currentImage));
  const hasPhoto = !!thumbUrl;
  const { zoom, position } = cropParamsFromAttrs(cropSource);

  const handleSave = (vals) => {
    setRemoved(false);
    form.setFieldValue(field.name, vals.avatar);
    setOpen(false);
  };

  // Remove the photo everywhere: clear the form value + any preview, delete the
  // media on the backend, drop it from the store, and refresh the tree so the
  // member row updates too.
  const handleRemove = async () => {
    setRemoved(true);
    form.setFieldValue(field.name, null);
    if (currentCover) {
      try {
        await destroy(`/v2/media/${currentCover}`);
      } catch (e) { /* already gone / ignore */ }
      receiveDeletion({ entityType: 'images', id: currentCover });
    }
    if (onRemoved) onRemoved();
  };

  const thumbnail = (
    <CroppedAvatar url={thumbUrl} size={40} zoom={zoom} position={position} circular />
  );

  const editRemove = (
    <Flex alignItems="center" sx={{ gap: '12px' }}>
      <Box as="button" type="button" onClick={() => setOpen(true)} aria-label="Edit photo"
        sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', color: FT_COLORS.primary }}>
        <FiEdit2 size={18} />
      </Box>
      <Box as="button" type="button" onClick={handleRemove} width={30} aria-label="Remove photo"
        sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', color: FT_COLORS.muted }}>
        <FiX size={18} />
      </Box>
    </Flex>
  );

  return (
    <Box onClick={(e) => {
      e.preventDefault()
      e.stopPropagation()
    }}>
      <FieldLabel>{label}</FieldLabel>
      {hasPhoto ? (
        <MediaRow leading={thumbnail} label="Profile photo" trailing={editRemove} />
      ) : (
        <MediaRow
          icon={FiUpload}
          label="Upload"
          onClick={() => setOpen(true)}
          trailing={<Box as={FiPlus} size={18} color={FT_COLORS.primary} />}
        />
      )}
      {open && (
        <EditPhotoModal
          currentCover={currentCover}
          circular={circular}
          value={value}
          onClose={() => setOpen(false)}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

PhotoField.propTypes = {
  field: PropTypes.shape({ name: PropTypes.string, value: PropTypes.any }).isRequired,
  form: PropTypes.shape({ setFieldValue: PropTypes.func }).isRequired,
  label: PropTypes.string,
  circular: PropTypes.bool,
  currentCover: PropTypes.string,
  currentImage: PropTypes.shape({}),
  onRemoved: PropTypes.func,
};

PhotoField.defaultProps = {
  label: 'Profile Photo',
  circular: true,
  currentCover: '',
  currentImage: null,
  onRemoved: null,
};

export default PhotoField;
